import React, { forwardRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Chip,
  Typography,
  Stack,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import isFunction from "lodash/isFunction";
import Flx from "../layout/Flx";
import AdvancedTableFilteringButton from "../../screens/LoanPipelineScreen/buttons/AdvancedTableFilteringButton";
import Txt from "../typography/Txt";

const AgGridAppliedFilters = forwardRef(
  (
    {
      onFilterRemove,
      onAllFiltersRemove,
      showTitle = true,
      collapsible = false,
      initialExpanded = true,
      maxWidth = "100%",
      advancedFiltering,
      onAdvancedFilteringClick,
    },
    ref
  ) => {
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [expanded, setExpanded] = useState(initialExpanded);
    const [gridReady, setGridReady] = useState(false);

    // Function to format filter values for display
    const formatFilterValue = (filter, columnDef) => {
      // Handle multi-filter structure
      if (filter.filterType === "multi" && filter.filterModels) {
        const activeFilters = filter.filterModels.filter(
          (model) => model !== null
        );
        const formattedFilters = activeFilters.map((model) =>
          formatSingleFilter(model)
        );
        return formattedFilters.join(" OR ");
      }

      // Handle single filter
      return formatSingleFilter(filter);
    };

    // Function to format a single filter model
    const formatSingleFilter = (filter) => {
      if (!filter) return "";

      const {
        type,
        filter: filterValue,
        filterType,
        condition1,
        condition2,
        values,
      } = filter;

      // Handle set filter (dropdown selections)
      if (filterType === "set" || type === "set" || values) {
        const selectedValues = values || filter.values || [];
        if (selectedValues.length === 0) return "No selections";
        if (selectedValues.length > 3) {
          return `${selectedValues.length} items selected`;
        }
        return selectedValues.join(", ");
      }

      // Handle text filters
      if (type === "text" || filterType === "text") {
        if (condition1 && condition2) {
          const cond1Text = `${condition1.type || "contains"}: "${condition1.filter}"`;
          const cond2Text = `${condition2.type || "contains"}: "${condition2.filter}"`;
          return `${cond1Text} AND ${cond2Text}`;
        }
        const textType = condition1?.type || filterType || "contains";
        const textValue = filterValue || condition1?.filter || "";
        return `${textType}: "${textValue}"`;
      }

      // Handle number filters
      if (type === "number" || filterType === "number") {
        if (condition1 && condition2) {
          const cond1Text = `${condition1.type}: ${condition1.filter}`;
          const cond2Text = `${condition2.type}: ${condition2.filter}`;
          return `${cond1Text} AND ${cond2Text}`;
        }
        const numberType = condition1?.type || filterType || "equals";
        const numberValue = filterValue || condition1?.filter || "";
        return `${numberType}: ${numberValue}`;
      }

      // Handle date filters
      if (type === "date" || filterType === "date") {
        if (condition1 && condition2) {
          const cond1Text = `${condition1.type}: ${formatDateValue(condition1)}`;
          const cond2Text = `${condition2.type}: ${formatDateValue(condition2)}`;
          return `${cond1Text} AND ${cond2Text}`;
        }
        const dateType = condition1?.type || filterType || "equals";
        const dateValue = formatDateValue(condition1 || filter);
        return `${dateType}: ${dateValue}`;
      }

      // Handle boolean filters
      if (type === "boolean" || filterType === "boolean") {
        return filterValue ? "True" : "False";
      }

      // Fallback for unknown filter types
      if (filterValue !== undefined) {
        return String(filterValue);
      }

      if (condition1?.filter !== undefined) {
        return String(condition1.filter);
      }

      // Last resort - show a simplified version
      return "Custom filter applied";
    };

    // Helper function to format date values
    const formatDateValue = (dateFilter) => {
      if (!dateFilter) return "";

      const dateValue =
        dateFilter.dateFrom || dateFilter.dateTo || dateFilter.filter;
      if (!dateValue) return "";

      try {
        return new Date(dateValue).toLocaleDateString();
      } catch {
        return String(dateValue);
      }
    };

    // Function to get column display name
    const getColumnDisplayName = (colId, columnDefs) => {
      const colDef = columnDefs?.find(
        (col) => col.field === colId || col.colId === colId
      );
      return colDef?.headerName || colDef?.field || colId;
    };

    // Function to extract applied filters from AG Grid
    const extractAppliedFilters = useCallback(() => {
      if (!ref?.current?.api || !gridReady) return [];

      try {
        const gridApi = ref.current.api;
        if (!gridApi.getFilterModel || !gridApi.getColumnDefs) return [];

        const filterModel = gridApi.getFilterModel();
        const columnDefs = gridApi.getColumnDefs();
        const filters = [];

        Object.entries(filterModel).forEach(([colId, filter]) => {
          if (filter && typeof filter === "object") {
            filters.push({
              columnId: colId,
              columnName: getColumnDisplayName(colId, columnDefs),
              filter: filter,
              displayValue: formatFilterValue(
                filter,
                columnDefs?.find((col) => col.field === colId)
              ),
            });
          }
        });

        return filters;
      } catch (error) {
        console.warn("Error extracting AG Grid filters:", error);
        return [];
      }
    }, [ref, gridReady]);

    // Function to remove a specific filter
    const handleFilterRemove = (columnId) => {
      if (!ref?.current?.api || !gridReady) return;

      try {
        const gridApi = ref.current.api;
        const currentFilterModel = gridApi.getFilterModel();

        // Remove the specific filter
        delete currentFilterModel[columnId];

        // Apply the updated filter model
        gridApi.setFilterModel(currentFilterModel);

        // Call custom onFilterRemove if provided
        if (isFunction(onFilterRemove)) {
          onFilterRemove(columnId, ref);
        }

        // Update the displayed filters
        setAppliedFilters(extractAppliedFilters());
      } catch (error) {
        console.warn("Error removing filter:", error);
      }
    };

    // Function to clear all filters
    const handleClearAllFilters = () => {
      if (!ref?.current?.api || !gridReady) return;

      try {
        const gridApi = ref.current.api;
        gridApi.setFilterModel(null);

        // Call custom onAllFiltersRemove if provided
        if (isFunction(onAllFiltersRemove)) {
          onAllFiltersRemove(ref);
        }

        setAppliedFilters([]);
      } catch (error) {
        console.warn("Error clearing all filters:", error);
      }
    };

    // Check if grid API is ready
    const checkGridReady = useCallback(() => {
      if (
        ref?.current?.api &&
        ref.current.api.getFilterModel &&
        ref.current.api.getColumnDefs &&
        ref.current.api.addEventListener
      ) {
        setGridReady(true);
        return true;
      }
      return false;
    }, [ref]);

    // Setup event listeners when grid is ready
    useEffect(() => {
      let intervalId;
      let isSetup = false;

      const setupEventListeners = () => {
        if (!checkGridReady() || isSetup) return false;

        try {
          const gridApi = ref.current.api;

          // Listen for filter changes
          const onFilterChanged = () => {
            setAppliedFilters(extractAppliedFilters());
          };

          // Listen for grid ready event
          const onGridReady = () => {
            setGridReady(true);
            setAppliedFilters(extractAppliedFilters());
          };

          // Add event listeners
          gridApi.addEventListener("filterChanged", onFilterChanged);
          gridApi.addEventListener("gridReady", onGridReady);

          // Initial load
          setAppliedFilters(extractAppliedFilters());

          isSetup = true;

          // Cleanup function
          return () => {
            if (gridApi && gridApi.removeEventListener) {
              gridApi.removeEventListener("filterChanged", onFilterChanged);
              gridApi.removeEventListener("gridReady", onGridReady);
            }
          };
        } catch (error) {
          console.warn("Error setting up AG Grid event listeners:", error);
          return false;
        }
      };

      // Try to setup immediately
      const cleanup = setupEventListeners();

      if (cleanup) {
        // If setup was successful, clear any interval and return cleanup
        if (intervalId) {
          clearInterval(intervalId);
        }
        return cleanup;
      }

      // If not ready, poll until it is
      intervalId = setInterval(() => {
        const cleanup = setupEventListeners();
        if (cleanup) {
          clearInterval(intervalId);
        }
      }, 100);

      // Cleanup interval on unmount
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
        if (cleanup && typeof cleanup === "function") {
          cleanup();
        }
      };
    }, [ref, extractAppliedFilters, checkGridReady]);

    // Update filters when gridReady changes
    useEffect(() => {
      if (gridReady) {
        setAppliedFilters(extractAppliedFilters());
      }
    }, [gridReady, extractAppliedFilters]);

    // Don't render if no filters are applied
    if (appliedFilters?.length === 0) {
      return null;
    }

    const FiltersContent = () => (
      <Flx ac gap={2}>
        <Flx ac jb gap={0.5}>
          <AdvancedTableFilteringButton
            advancedFiltering={advancedFiltering}
            setAdvancedFiltering={onAdvancedFilteringClick}
            sx={{
              svg: {
                fontSize: "15px",
              },
            }}
          />
          {showTitle && (
            <Txt sx={{ fontSize: "11px" }} secondary>
              Applied Filters ({appliedFilters.length})
            </Txt>
          )}
        </Flx>

        <Flx g={1} wrap>
          {appliedFilters.map(({ columnId, columnName, displayValue }) => (
            <Chip
              key={columnId}
              label={
                <Box>
                  <Txt
                    component="span"
                    variant="caption"
                    fontWeight="bold"
                    sx={{ textTransform: "capitalize", fontSize: "11px" }}
                  >
                    {columnName}:
                  </Txt>{" "}
                  <Txt
                    component="span"
                    variant="caption"
                    sx={{ fontSize: "11px" }}
                  >
                    {displayValue}
                  </Txt>
                </Box>
              }
              onDelete={() => handleFilterRemove(columnId)}
              deleteIcon={<ClearIcon />}
              variant="outlined"
              size="small"
              color="primary"
              sx={{
                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ))}
          {appliedFilters?.length > 1 && (
            <Chip
              label={"Clear All"}
              onDelete={handleClearAllFilters}
              deleteIcon={<ClearIcon />}
              variant="outlined"
              size="small"
              color="error"
            />
          )}
        </Flx>
      </Flx>
    );

    if (collapsible) {
      return (
        <Box sx={{ maxWidth }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: "pointer", mb: expanded ? 1 : 0 }}
            onClick={() => setExpanded(!expanded)}
          >
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              Applied Filters ({appliedFilters.length})
            </Typography>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </Stack>

          <Collapse in={expanded}>
            <Box sx={{ pl: 4 }}>
              <FiltersContent />
            </Box>
          </Collapse>

          {!expanded && <Divider sx={{ mt: 1 }} />}
        </Box>
      );
    }
    return <FiltersContent />;
  }
);

AgGridAppliedFilters.displayName = "AgGridAppliedFilters";

export default AgGridAppliedFilters;
